import type { ToolResult } from "@/types/tools";

export type ColorConversion = {
  red: number;
  green: number;
  blue: number;
  alpha: number;
  hex: string;
  hexa: string;
  rgb: string;
  rgba: string;
  hsl: string;
  hsla: string;
};

type RgbaColor = Pick<ColorConversion, "red" | "green" | "blue" | "alpha">;

export function convertColor(input: string): ToolResult<ColorConversion> {
  const trimmed = input.trim();

  if (!trimmed) {
    return {
      ok: false,
      error: "Enter a HEX, RGB, or RGBA color first.",
    };
  }

  const color = parseHexColor(trimmed) ?? parseRgbColor(trimmed);

  if (!color) {
    return {
      ok: false,
      error:
        "Use a color like #2563eb, #38bdf8cc, rgb(37, 99, 235), or rgba(37, 99, 235, 0.8).",
    };
  }

  const hsl = rgbToHsl(color);
  const alpha = roundAlpha(color.alpha);

  return {
    ok: true,
    value: {
      red: color.red,
      green: color.green,
      blue: color.blue,
      alpha,
      hex: formatHex(color),
      hexa: `${formatHex(color)}${toHexByte(alpha * 255)}`,
      rgb: `rgb(${color.red}, ${color.green}, ${color.blue})`,
      rgba: `rgba(${color.red}, ${color.green}, ${color.blue}, ${formatAlpha(alpha)})`,
      hsl: `hsl(${hsl.hue}, ${hsl.saturation}%, ${hsl.lightness}%)`,
      hsla: `hsla(${hsl.hue}, ${hsl.saturation}%, ${hsl.lightness}%, ${formatAlpha(alpha)})`,
    },
  };
}

function parseHexColor(input: string): RgbaColor | null {
  const value = input.startsWith("#") ? input.slice(1) : input;

  if (!/^[\da-f]{3,8}$/i.test(value) || ![3, 4, 6, 8].includes(value.length)) {
    return null;
  }

  const expanded =
    value.length <= 4
      ? value
          .split("")
          .map((character) => character + character)
          .join("")
      : value;

  return {
    red: parseInt(expanded.slice(0, 2), 16),
    green: parseInt(expanded.slice(2, 4), 16),
    blue: parseInt(expanded.slice(4, 6), 16),
    alpha: expanded.length === 8 ? parseInt(expanded.slice(6, 8), 16) / 255 : 1,
  };
}

function parseRgbColor(input: string): RgbaColor | null {
  const match = input.match(/^rgba?\((.+)\)$/i);

  if (!match) {
    return null;
  }

  const parts = match[1].split(",").map((part) => part.trim());

  if (![3, 4].includes(parts.length)) {
    return null;
  }

  const channels = parts.slice(0, 3).map(parseRgbChannel);
  const alpha = parts.length === 4 ? parseAlpha(parts[3]) : 1;

  if (channels.some((channel) => channel === null) || alpha === null) {
    return null;
  }

  return {
    red: channels[0] ?? 0,
    green: channels[1] ?? 0,
    blue: channels[2] ?? 0,
    alpha,
  };
}

function parseRgbChannel(input: string): number | null {
  if (!input) {
    return null;
  }

  const value = Number(input);

  if (!Number.isFinite(value) || value < 0 || value > 255) {
    return null;
  }

  return Math.round(value);
}

function parseAlpha(input: string): number | null {
  if (!input) {
    return null;
  }

  if (input.endsWith("%")) {
    const percent = Number(input.slice(0, -1));

    if (!Number.isFinite(percent) || percent < 0 || percent > 100) {
      return null;
    }

    return percent / 100;
  }

  const value = Number(input);

  if (!Number.isFinite(value) || value < 0 || value > 1) {
    return null;
  }

  return value;
}

function rgbToHsl({ red, green, blue }: RgbaColor) {
  const normalizedRed = red / 255;
  const normalizedGreen = green / 255;
  const normalizedBlue = blue / 255;
  const max = Math.max(normalizedRed, normalizedGreen, normalizedBlue);
  const min = Math.min(normalizedRed, normalizedGreen, normalizedBlue);
  const delta = max - min;
  const lightness = (max + min) / 2;

  if (delta === 0) {
    return {
      hue: 0,
      saturation: 0,
      lightness: Math.round(lightness * 100),
    };
  }

  const saturation = delta / (1 - Math.abs(2 * lightness - 1));
  let hue = 0;

  if (max === normalizedRed) {
    hue = ((normalizedGreen - normalizedBlue) / delta) % 6;
  } else if (max === normalizedGreen) {
    hue = (normalizedBlue - normalizedRed) / delta + 2;
  } else {
    hue = (normalizedRed - normalizedGreen) / delta + 4;
  }

  return {
    hue: Math.round(hue * 60 + (hue < 0 ? 360 : 0)),
    saturation: Math.round(saturation * 100),
    lightness: Math.round(lightness * 100),
  };
}

function formatHex(color: RgbaColor) {
  return `#${toHexByte(color.red)}${toHexByte(color.green)}${toHexByte(color.blue)}`;
}

function toHexByte(value: number) {
  return Math.round(value).toString(16).padStart(2, "0");
}

function formatAlpha(value: number) {
  return roundAlpha(value).toString();
}

function roundAlpha(value: number) {
  return Math.round(value * 1000) / 1000;
}
